import { useState } from 'react';

const researchData = {
  zinc: [
    {
      source: 'Frontiers in Oncology (2020)',
      finding: '50세 이상 남성의 아연 결핍을 체계적으로 분석한 결과, 아연 부족이 전립선 질환(전립선비대, 전립선염, 전립선암)의 발생과 진행에 기여할 수 있음을 보고했습니다.',
    },
    {
      source: 'PLOS ONE 메타분석 (2024)',
      finding: '52개 연구, 총 163,909명을 분석한 체계적 문헌고찰에서 아연의 적정 수준 유지가 전립선암 예방에 중요함을 확인했습니다.',
    },
    {
      source: 'Eur J Epidemiol 30년 추적 (2022)',
      finding: '47,240명을 30년간 추적한 결과, 아연 부족(&lt;9mg/일)과 과잉(&gt;65mg/일) 모두 치명적 전립선암 위험 증가. 적정 섭취량은 12–15mg/일로 권고됩니다.',
    },
  ],
  vitaminD: [
    {
      source: 'Endocrine Connections 용량-반응 메타분석 (2018)',
      finding: '혈중 비타민 D가 20nmol/L 증가할 때마다 전립선암 특이적 사망률이 9% 감소했습니다(HR 0.91). 비타민 D 수준이 높을수록 생존에 유리합니다.',
    },
    {
      source: '덴마크 코호트 연구',
      finding: '비타민 D 결핍 상태의 전립선암 환자는 전립선암 특이적 사망률과 기타 원인 사망률 모두에서 나쁜 예후를 보였습니다.',
    },
    {
      source: 'Oncology Reports 전향적 연구 (2022)',
      finding: '비타민 D 결핍 환자는 정상 수준 환자 대비 전립선암 발병 위험이 유의미하게 높았습니다.',
    },
  ],
  omega3: [
    {
      source: 'Mayo Clinic Proceedings 종합 리뷰 (2014)',
      finding: '전임상 데이터와 역학 연구를 종합한 결과, 오메가-3의 항염증 효과가 전립선암 예방 및 치료에 도움이 될 수 있음을 제시했습니다.',
    },
    {
      source: 'Frontiers in Nutrition (2025)',
      finding: 'PLCO 선별검사 시험 데이터 분석에서 오메가-3 식이 섭취가 전립선암 진행을 늦추고 사망률을 감소시킬 수 있다는 근거가 보고되었습니다.',
    },
    {
      source: 'Communications Medicine 임상시험 (2024)',
      finding: '제2b상 무작위 배정 시험에서 전립선 조직 내 EPA 농도가 높은 능동감시 환자는 암 등급 상승 위험이 유의미하게 감소했습니다.',
    },
  ],
};

const nutrients = [
  {
    key: 'zinc',
    icon: 'ri-shield-check-line',
    name: '아연 (Zinc)',
    tag: '면역·세포 수복',
    summary: '전립선은 인체에서 가장 많은 아연을 축적하는 장기 중 하나입니다. 아연은 면역 기능, 세포 분열, DNA 수복에 필수적이며 전립선 건강과 밀접하게 연관됩니다.',
    recommendation: '호박씨, 캐슈너트, 렌틸콩, 통곡물 등 식물성 공급원 활용. 필요시 저용량 보충제(하루 12–15mg). 65mg/일 이상 과다 복용 주의.',
    recommendIcon: 'ri-seedling-line',
    color: '#2d6a4f',
    bg: '#f0faf4',
    tagBg: '#d8f3dc',
  },
  {
    key: 'vitaminD',
    icon: 'ri-sun-line',
    name: '비타민 D',
    tag: '면역·세포 성장 조절',
    summary: '유제품은 비타민 D가 강화된 주요 식품원입니다. 피탄산 제한 식단을 따르면 비타민 D 결핍 위험이 높아지며, 이는 전립선암 예후와 직결됩니다.',
    recommendation: '일광욕 시간 확보, 비타민 D 강화 식물성 음료(두유, 오렌지주스) 활용. 혈액 검사로 25(OH)D 수치 정기 확인 후 의료진 상의.',
    recommendIcon: 'ri-sun-foggy-line',
    color: '#b5600a',
    bg: '#fff8f0',
    tagBg: '#ffe8cc',
  },
  {
    key: 'omega3',
    icon: 'ri-water-flash-line',
    name: '오메가-3 지방산',
    tag: '항염증·암 예방',
    summary: '오메가-3(EPA, DHA)는 항염증 작용을 통해 암 예방에 기여합니다. 피탄산 제한 식단에서 전반적인 지방 섭취 감소로 부족해질 수 있습니다.',
    recommendation: '생선(연어, 고등어, 참치, 정어리)은 피탄산이 낮고 오메가-3가 풍부. 주 2회 이상 섭취 권장. 식물성 대안: 호두, 치아씨, 아마씨유.',
    recommendIcon: 'ri-drop-line',
    color: '#1a5276',
    bg: '#f0f6ff',
    tagBg: '#d6eaf8',
  },
];

type TabKey = 'zinc' | 'vitaminD' | 'omega3';

const NutritionSection = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('zinc');
  const [openRef, setOpenRef] = useState(false);

  const references = [
    'Wright ME et al. Estimated phytanic acid intake and prostate cancer risk. Int J Cancer. 2012;131(6):1396-406.',
    'Price AJ et al. Plasma phytanic acid concentration and risk of prostate cancer: results from EPIC. Eur J Cancer. 2010;46(18):3370-4.',
    'Mondul AM et al. Serum phytanic and pristanic acid levels and prostate cancer risk in Finnish smokers. Cancer Epidemiol Biomarkers Prev. 2014.',
    'Prasad AS. Zinc deficiency in men over 50 and its implications in prostate disorders. Front Oncol. 2020;10:1293.',
    'Mahmoud AM et al. The association between zinc and prostate cancer: a systematic review and meta-analysis. PLOS ONE. 2024;19(3):e0299398.',
    'Epstein MM et al. Zinc supplement use and risk of aggressive prostate cancer: a 30-year follow-up study. Eur J Epidemiol. 2022;37:1237-1250.',
    'Xu Y et al. Circulating vitamin D level and mortality in prostate cancer patients: a dose-response meta-analysis. Endocrine Connections. 2018;7(12):R294-R303.',
    'Szymanski KM et al. Fish-derived omega-3 fatty acids and prostate cancer: a systematic review. Integr Cancer Ther. 2017;16(1):32-44.',
    'Lelong H et al. Dietary omega-3 intake may slow prostate cancer progression: evidence from PLCO. Front Nutr. 2025.',
    'Guertin MH et al. A phase IIb randomized placebo-controlled trial testing the effect of MAG-EPA on prostate cancer proliferation. Commun Med. 2024;4:34.',
    'Baldwin C et al. Safety of long-term restrictive diets for peroxisomal disorders. Br J Nutr. 2016;115(10):1830-6.',
  ];

  const activeNutrient = nutrients.find((n) => n.key === activeTab)!;
  const activeResearch = researchData[activeTab];

  return (
    <section id="nutrition" className="py-24 md:py-32 bg-[#f8f6f2]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-0.5 bg-[#1a2b4a]" />
          <span className="text-[#1a2b4a] text-xs font-semibold tracking-[0.25em] uppercase">Nutrition & Cancer</span>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4">
          <h2 className="text-3xl md:text-5xl font-bold text-[#1a2b4a] leading-tight">
            전립선암과 영양소
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed max-w-md lg:text-right">
            2026년 4월 | 학술 연구 기반 요약
          </p>
        </div>

        {/* Phytanic Acid Intro */}
        <div className="mt-10 mb-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#1a2b4a]/8">
                <i className="ri-flask-line text-[#1a2b4a] text-lg" />
              </div>
              <h3 className="text-base font-bold text-[#1a2b4a]">피탄산이란?</h3>
            </div>
            <p className="text-sm text-gray-500 leading-relaxed mb-4">
              피탄산(phytanic acid)은 소, 양, 염소 같은 반추동물의 유제품(버터, 치즈, 전유)과 붉은 고기(쇠고기, 양고기)에 풍부한 포화지방산입니다. 반추동물의 장내 세균이 엽록소를 분해하여 피톨을 만들고, 이것이 피탄산으로 전환되어 지방 조직과 우유에 축적됩니다.
            </p>
            <div className="flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2.5">
              <i className="ri-information-line text-[#1a2b4a] text-sm mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-500 leading-relaxed">식물성 식품에는 피탄산이 포함되어 있지 않습니다.</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50">
                <i className="ri-alert-line text-red-500 text-lg" />
              </div>
              <h3 className="text-base font-bold text-[#1a2b4a]">전립선암과 피탄산의 관계</h3>
            </div>
            <ul className="flex flex-col gap-3">
              {[
                { label: 'EPIC 코호트 연구 (2010)', desc: '유럽 7개국 대규모 전향적 코호트 연구에서 혈장 피탄산 농도와 유제품 지방 섭취 간 유의미한 연관성 확인.' },
                { label: 'Health Professionals Follow-up Study (2012)', desc: '높은 피탄산 섭취는 진행성 전립선암 위험 증가와 관련. 특히 버터 유래 피탄산이 주된 요인.' },
                { label: 'AMACR 과발현', desc: '피탄산 대사 효소 AMACR은 전립선암 조직에서 일관되게 과발현되어 현재 전립선암 진단 바이오마커로 사용.' },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1a2b4a] mt-2 flex-shrink-0" />
                  <div>
                    <span className="text-xs font-semibold text-[#1a2b4a]">{item.label}: </span>
                    <span className="text-xs text-gray-500 leading-relaxed">{item.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Deficiency Risk Banner */}
        <div className="mb-10 rounded-xl bg-[#1a2b4a] px-8 py-7 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0">
            <i className="ri-error-warning-line text-[#f5e6c8] text-xl" />
          </div>
          <div>
            <p className="text-[#f5e6c8] text-sm font-semibold mb-1">피탄산 제한 식단의 영양소 결핍 위험</p>
            <p className="text-white/75 text-sm leading-relaxed">
              유제품과 붉은 고기를 제한하면 <strong className="text-white">아연, 비타민 D, 오메가-3</strong>가 동시에 부족해질 수 있습니다.
              성인 레프썸병 환자 대상 장기 제한 식이 연구(Br J Nutr, 2016)에서도 이러한 미량영양소 모니터링의 중요성이 강조되었습니다.
            </p>
          </div>
        </div>

        {/* Nutrient Tabs */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          {nutrients.map((n) => (
            <button
              key={n.key}
              onClick={() => setActiveTab(n.key as TabKey)}
              className={`flex-1 flex items-center gap-3 px-5 py-4 rounded-xl border transition-all duration-200 cursor-pointer text-left ${
                activeTab === n.key
                  ? 'border-[#1a2b4a] bg-[#1a2b4a] text-white'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-[#1a2b4a]/30'
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${activeTab === n.key ? 'bg-white/15' : 'bg-gray-100'}`}>
                <i className={`${n.icon} text-base ${activeTab === n.key ? 'text-white' : 'text-[#1a2b4a]'}`} />
              </div>
              <span className="text-sm font-semibold whitespace-nowrap">{n.name}</span>
            </button>
          ))}
        </div>

        {/* Nutrient Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
          {/* Left: Summary + Recommendation */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div className="bg-white rounded-xl p-7 border border-gray-100 flex-1">
              <span
                className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-4"
                style={{ backgroundColor: activeNutrient.tagBg, color: activeNutrient.color }}
              >
                {activeNutrient.tag}
              </span>
              <h3 className="text-lg font-bold text-[#1a2b4a] mb-3">{activeNutrient.name}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{activeNutrient.summary}</p>
            </div>
            <div className="bg-white rounded-xl p-7 border border-gray-100" style={{ borderLeftWidth: 3, borderLeftColor: activeNutrient.color }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-md" style={{ backgroundColor: activeNutrient.bg }}>
                  <i className={`${activeNutrient.recommendIcon} text-sm`} style={{ color: activeNutrient.color }} />
                </div>
                <p className="text-sm font-bold text-[#1a2b4a]">실천 권고</p>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">{activeNutrient.recommendation}</p>
            </div>
          </div>

          {/* Right: Research Table */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-7 py-5 border-b border-gray-100 flex items-center gap-2">
              <i className="ri-article-line text-[#1a2b4a] text-base" />
              <h4 className="text-sm font-bold text-[#1a2b4a]">학술 연구 근거</h4>
            </div>
            <div className="divide-y divide-gray-50">
              {activeResearch.map((r, i) => (
                <div key={i} className="px-7 py-5 flex flex-col gap-2">
                  <p className="text-xs font-semibold text-[#1a2b4a] tracking-wide">{r.source}</p>
                  <p
                    className="text-sm text-gray-500 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: r.finding }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Conclusion */}
        <div className="mt-10 bg-white rounded-xl p-8 md:p-10 border border-gray-100">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#1a2b4a]/8">
              <i className="ri-bookmark-line text-[#1a2b4a] text-lg" />
            </div>
            <h3 className="text-base font-bold text-[#1a2b4a]">결론</h3>
          </div>
          <p className="text-sm text-gray-500 leading-relaxed mb-5">
            피탄산 제한 식단은 전립선암 예방을 위한 유망한 접근법이지만, 유제품과 붉은 고기를 제한하면서 <strong className="text-[#1a2b4a]">아연, 비타민 D, 오메가-3</strong>와 같은 핵심 영양소가 부족해질 수 있습니다.
            이 세 가지 영양소는 모두 전립선암 환자의 예후와 밀접하게 관련된다는 학술적 근거가 있으므로, 피탄산을 줄이면서도 이들 영양소를 충분히 보충하는 균형 잡힌 식이 전략이 필수적입니다.
          </p>
          <div className="flex items-start gap-2 bg-amber-50 rounded-lg px-4 py-3">
            <i className="ri-stethoscope-line text-amber-600 text-sm mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700 leading-relaxed">
              본 내용은 학술 연구 결과를 일반인 눈높이에서 요약한 것으로, 개인의 영양 보충제 복용에 대해서는 반드시 담당 의료진과 상의하시기 바랍니다.
            </p>
          </div>
        </div>

        {/* References Toggle */}
        <div className="mt-6">
          <button
            onClick={() => setOpenRef(!openRef)}
            className="flex items-center gap-2 text-xs text-gray-400 hover:text-[#1a2b4a] transition-colors duration-200 cursor-pointer"
          >
            <i className={`${openRef ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`} />
            참고 문헌 {openRef ? '접기' : '보기'} ({references.length}건)
          </button>
          {openRef && (
            <div className="mt-4 bg-white rounded-xl border border-gray-100 px-7 py-6">
              <ol className="flex flex-col gap-2">
                {references.map((ref, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="text-xs text-gray-400 font-semibold flex-shrink-0 mt-0.5">{i + 1}.</span>
                    <p className="text-xs text-gray-400 leading-relaxed">{ref}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-2xl bg-[#1a2b4a] px-8 py-10 md:px-14 md:py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-[#f5e6c8] text-xs font-semibold tracking-[0.2em] uppercase mb-2">전문가 설계</p>
            <h3 className="text-white text-xl md:text-2xl font-bold leading-snug">
              프로엑티브케어는 아연, 비타민 D, 오메가-3를<br className="hidden md:block" /> 최적 비율로 담았습니다.
            </h3>
          </div>
          <button
            onClick={() => {
              const el = document.getElementById('products');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-white text-[#1a2b4a] text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-gray-100 transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            제품 보러가기
          </button>
        </div>

      </div>
    </section>
  );
};

export default NutritionSection;
